import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { useAuth } from '../../contexts/AuthContext';
import commentService from '../../services/commentService';
import { FiMessageCircle, FiSend, FiThumbsUp, FiFlag } from 'react-icons/fi';

const CommentsContainer = styled.div`
  margin-top: ${props => props.theme.spacing.xxl};
  padding-top: ${props => props.theme.spacing.xxl};
  border-top: 1px solid ${props => props.theme.colors.divider};
`;

const CommentsHeader = styled.div`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.md};
  margin-bottom: ${props => props.theme.spacing.xl};
  
  h3 {
    font-size: 1.5rem;
    color: ${props => props.theme.colors.text};
    margin: 0;
  }
  
  .comment-count {
    background: ${props => props.theme.colors.primary};
    color: ${props => props.theme.colors.background};
    padding: ${props => props.theme.spacing.xs} ${props => props.theme.spacing.sm};
    border-radius: ${props => props.theme.borderRadius.full};
    font-size: 0.8rem;
    font-weight: 600;
  }
`;

const CommentForm = styled.form`
  background: ${props => props.theme.colors.surface};
  border: 1px solid ${props => props.theme.colors.divider};
  border-radius: ${props => props.theme.borderRadius.lg};
  padding: ${props => props.theme.spacing.lg};
  margin-bottom: ${props => props.theme.spacing.xl};
`;

const CommentTextarea = styled.textarea`
  width: 100%;
  min-height: 100px;
  padding: ${props => props.theme.spacing.md};
  border: 1px solid ${props => props.theme.colors.divider};
  border-radius: ${props => props.theme.borderRadius.md};
  font-family: ${props => props.theme.fonts.body};
  font-size: 1rem;
  resize: vertical;
  margin-bottom: ${props => props.theme.spacing.md};
  
  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.primary};
  }
  
  &::placeholder {
    color: ${props => props.theme.colors.textLight};
  }
`;

const CommentFormFooter = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  
  .form-info {
    font-size: 0.8rem;
    color: ${props => props.theme.colors.textSecondary};
  }
`;

const SubmitButton = styled.button`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.sm};
  background: ${props => props.theme.colors.primary};
  color: ${props => props.theme.colors.background};
  border: none;
  border-radius: ${props => props.theme.borderRadius.md};
  padding: ${props => props.theme.spacing.sm} ${props => props.theme.spacing.lg};
  font-family: ${props => props.theme.fonts.accent};
  font-weight: 600;
  cursor: pointer;
  transition: all ${props => props.theme.transitions.normal};
  
  &:hover:not(:disabled) {
    background: ${props => props.theme.colors.darkGreen};
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const CommentsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing.lg};
`;

const Comment = styled.div`
  background: ${props => props.theme.colors.background};
  border: 1px solid ${props => props.theme.colors.divider};
  border-radius: ${props => props.theme.borderRadius.lg};
  padding: ${props => props.theme.spacing.lg};
`;

const CommentHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: ${props => props.theme.spacing.md};
  
  .comment-author {
    display: flex;
    align-items: center;
    gap: ${props => props.theme.spacing.md};
    
    .author-avatar {
      width: 40px;
      height: 40px;
      background: ${props => props.theme.colors.primary};
      color: ${props => props.theme.colors.background};
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      font-weight: 600;
      font-size: 0.9rem;
    }
    
    .author-info {
      .name {
        font-weight: 600;
        color: ${props => props.theme.colors.text};
        margin-bottom: 2px;
      }
      
      .date {
        font-size: 0.8rem;
        color: ${props => props.theme.colors.textSecondary};
      }
    }
  }
  
  .comment-actions {
    display: flex;
    gap: ${props => props.theme.spacing.sm};
  }
`;

const CommentAction = styled.button`
  background: none;
  border: none;
  color: ${props => props.theme.colors.textSecondary};
  cursor: pointer;
  padding: ${props => props.theme.spacing.xs};
  border-radius: ${props => props.theme.borderRadius.sm};
  transition: all ${props => props.theme.transitions.normal};
  
  &:hover {
    color: ${props => props.theme.colors.primary};
    background: rgba(46, 125, 50, 0.1);
  }
`;

const CommentContent = styled.div`
  color: ${props => props.theme.colors.text};
  line-height: 1.6;
  white-space: pre-wrap;
`;

const LoginPrompt = styled.div`
  background: ${props => props.theme.colors.surface};
  border: 1px solid ${props => props.theme.colors.divider};
  border-radius: ${props => props.theme.borderRadius.lg};
  padding: ${props => props.theme.spacing.xl};
  text-align: center;
  margin-bottom: ${props => props.theme.spacing.xl};
  
  p {
    margin-bottom: ${props => props.theme.spacing.md};
    color: ${props => props.theme.colors.textSecondary};
  }
  
  a {
    color: ${props => props.theme.colors.primary};
    font-weight: 600;
    text-decoration: none;
    
    &:hover {
      text-decoration: underline;
    }
  }
`;

const CommentSection = ({ articleId }) => {
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const { currentUser } = useAuth();

  const loadComments = useCallback(async () => {
    try {
      setLoading(true);
      const commentsData = await commentService.getComments(articleId);
      setComments(commentsData);
    } catch (error) {
      console.error('Error loading comments:', error);
    } finally {
      setLoading(false);
    }
  }, [articleId]);

  useEffect(() => {
    loadComments();
  }, [loadComments]);

  const handleSubmitComment = async (e) => {
    e.preventDefault();
    
    if (!newComment.trim() || !currentUser) return;

    try {
      setSubmitting(true);
      
      await commentService.addComment(articleId, {
        content: newComment.trim(),
        author: currentUser.displayName || currentUser.email,
        author_id: currentUser.id,
        author_email: currentUser.email
      });

      setNewComment('');
      // Don't reload comments immediately since they need approval
      alert('Your comment has been submitted and is pending approval.');
    } catch (error) {
      console.error('Error submitting comment:', error);
      alert('Failed to submit comment. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const getUserInitials = (name) => {
    if (!name) return '?';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return '';
    const date = new Date(timestamp);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <CommentsContainer>
      <CommentsHeader>
        <FiMessageCircle size={24} />
        <h3>Comments</h3>
        <span className="comment-count">{comments.length}</span>
      </CommentsHeader>

      {currentUser ? (
        <CommentForm onSubmit={handleSubmitComment}>
          <CommentTextarea
            placeholder="Share your thoughts on this article..."
            value={newComment}
            onChange={(e) => setNewComment(e.target.value)}
            required
          />
          <CommentFormFooter>
            <div className="form-info">
              Comments are moderated and will appear after approval.
            </div>
            <SubmitButton type="submit" disabled={submitting || !newComment.trim()}>
              <FiSend size={16} />
              {submitting ? 'Submitting...' : 'Post Comment'}
            </SubmitButton>
          </CommentFormFooter>
        </CommentForm>
      ) : (
        <LoginPrompt>
          <p>Please <a href="/login">sign in</a> to leave a comment.</p>
        </LoginPrompt>
      )}

      <CommentsList>
        {loading ? (
          <div>Loading comments...</div>
        ) : comments.length > 0 ? (
          comments.map((comment) => (
            <Comment key={comment.id}>
              <CommentHeader>
                <div className="comment-author">
                  <div className="author-avatar">
                    {getUserInitials(comment.author)}
                  </div>
                  <div className="author-info">
                    <div className="name">{comment.author}</div>
                    <div className="date">{formatDate(comment.createdAt)}</div>
                  </div>
                </div>
                <div className="comment-actions">
                  <CommentAction title="Like comment">
                    <FiThumbsUp size={16} />
                  </CommentAction>
                  <CommentAction title="Report comment">
                    <FiFlag size={16} />
                  </CommentAction>
                </div>
              </CommentHeader>
              <CommentContent>{comment.content}</CommentContent>
            </Comment>
          ))
        ) : (
          <div style={{ textAlign: 'center', color: '#666', fontStyle: 'italic' }}>
            No comments yet. Be the first to share your thoughts!
          </div>
        )}
      </CommentsList>
    </CommentsContainer>
  );
};

export default CommentSection;